import { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import {
  postLogin,
  postVerifyOTP,
  getChatwootToken,
  ReducerState as ChatReducerState,
} from '@/global-states/chat';
import { CHAT_SET_CHATWOOT_READY } from '@/global-states/constants';

import ChatFab from './chat-fab';
import ChatwootWidget from './chatwoot-widget';
import LoginDrawer from './login-drawer';

type FlowStep = 'idle' | 'phone' | 'otp' | 'verifying' | 'chatwoot-active';

const ChatContainer = () => {
  const [flowStep, setFlowStep] = useState<FlowStep>('idle');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobile, setMobile] = useState('');

  const dispatch = useDispatch();

  // Track whether login flow was initiated in this session (not from persisted state)
  const loginFlowActive = useRef(false);

  const {
    loginSession,
    loginSessionState,
    loginSessionError,
    verification,
    verificationState,
    verificationError,
    chatAuthToken,
    chatwootToken,
    chatwootTokenState,
  } = useSelector(
    ({ chat }: { chat: ChatReducerState }) => ({
      loginSession: chat.loginSession,
      loginSessionState: chat.loginSessionState,
      loginSessionError: chat.loginSessionError,
      verification: chat.verification,
      verificationState: chat.verificationState,
      verificationError: chat.verificationError,
      chatAuthToken: chat.chatAuthToken,
      chatwootToken: chat.chatwootToken,
      chatwootTokenState: chat.chatwootTokenState,
    }),
    shallowEqual
  );

  // If already authenticated from a previous session, skip to chatwoot
  useEffect(() => {
    if (chatAuthToken && chatwootToken) {
      setFlowStep('chatwoot-active');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle FAB click
  const handleFabClick = useCallback(() => {
    if (chatAuthToken && chatwootToken) {
      setFlowStep('chatwoot-active');
      return;
    }
    if (chatAuthToken && !chatwootToken) {
      // Already verified but hash failed — retry without re-login
      setError(null);
      dispatch(getChatwootToken(chatAuthToken));
      return;
    }
    setError(null);
    setDrawerOpen(true);
    setFlowStep('phone');
  }, [chatAuthToken, chatwootToken, dispatch]);

  const handleSubmitPhone = useCallback(
    (submitted: string) => {
      setError(null);
      setMobile(submitted);
      loginFlowActive.current = true;
      dispatch(postLogin(submitted));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!loginFlowActive.current) return;
    if (loginSessionState === 'success' && loginSession?.session_id) {
      setFlowStep('otp');
      setDrawerOpen(true);
    }
    if (loginSessionState === 'error') {
      setError(loginSessionError?.msg || 'Could not send the code. Please try again.');
    }
  }, [loginSessionState, loginSession, loginSessionError?.msg]);

  const handleSubmitOtp = useCallback(
    (otp: string) => {
      if (!loginSession) return;
      setError(null);
      setFlowStep('verifying');
      dispatch(
        postVerifyOTP({
          id: loginSession.id,
          session_id: loginSession.session_id,
          otp,
        })
      );
    },
    [loginSession, dispatch]
  );

  const handleResend = useCallback(() => {
    if (!mobile) return;
    setError(null);
    dispatch(postLogin(mobile));
  }, [mobile, dispatch]);

  const handleBack = useCallback(() => {
    setError(null);
    setFlowStep('phone');
  }, []);

  // /v1/iam/verify returns a token on success and a non-2xx on a wrong code,
  // so there is no is_verification_completed flag to branch on.
  useEffect(() => {
    if (!loginFlowActive.current) return;
    if (verificationState === 'success' && verification?.token) {
      setDrawerOpen(false);
      dispatch(getChatwootToken(verification.token));
    } else if (verificationState === 'success') {
      // A 2xx /verify with no token is unreachable against today's backend,
      // but guard against it silently leaving flowStep stuck on 'verifying'.
      setError('Something went wrong. Please try again.');
      setFlowStep('otp');
    }
    if (verificationState === 'error') {
      setError(verificationError?.msg || 'That code was not correct. Please try again.');
      setFlowStep('otp');
    }
  }, [verificationState, verification, verificationError?.msg, dispatch]);

  // When chatwoot token is obtained, activate chatwoot
  useEffect(() => {
    if (chatwootTokenState === 'success' && chatwootToken) {
      setFlowStep('chatwoot-active');
      loginFlowActive.current = false;
      dispatch({ type: CHAT_SET_CHATWOOT_READY, payload: true });
    }
    if (chatwootTokenState === 'error') {
      // Don't go back to login — user is already verified
      // Close drawer, show FAB. Clicking FAB will retry hash.
      setDrawerOpen(false);
      setFlowStep('idle');
    }
  }, [chatwootTokenState, chatwootToken, dispatch]);

  const showFab = flowStep !== 'chatwoot-active';

  const drawerStep = flowStep === 'otp' || flowStep === 'verifying'
    ? ('otp' as const)
    : ('phone' as const);

  return (
    <>
      <ChatFab onClick={handleFabClick} visible={showFab && !drawerOpen} />

      <LoginDrawer
        open={drawerOpen}
        setOpen={setDrawerOpen}
        step={drawerStep}
        onSubmitPhone={handleSubmitPhone}
        onSubmitOtp={handleSubmitOtp}
        onResend={handleResend}
        onBack={handleBack}
        isLoading={
          loginSessionState === 'loading' || verificationState === 'loading'
        }
        error={error}
        mobile={mobile}
      />

      {flowStep === 'chatwoot-active' && chatwootTokenState === 'success' && chatwootToken && (
        <ChatwootWidget
          websiteToken={chatwootToken.ChatWoot?.Token || ''}
          baseUrl={chatwootToken.ChatWoot?.Url || ''}
          userIdentifier={chatwootToken.User?.Uid || ''}
          userId={chatwootToken.User?.Id || 0}
          hmacToken={chatwootToken.User?.Hash || ''}
          userName={chatwootToken.User?.Name || verification?.nickname}
          userEmail={chatwootToken.User?.Email}
          userPhone={chatwootToken.User?.Mobile || mobile}
          country={chatwootToken.User?.Country}
          timezone={chatwootToken.User?.Timezone}
        />
      )}
    </>
  );
};

export default ChatContainer;
